import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import api from "../Utils/api";
import Spinner from "../Components/Spinner";

export default function Documents() {
  const user = useSelector((state) => state.user.user);
  const [isLoading, setIsLoading] = useState(false);
  const [documents, setDocuments] = useState([]);

  useEffect(() => {
    if (user) {
      getMyDocuments();
    }
  }, [user]);

  const getMyDocuments = async () => {
    try {
      setIsLoading(true);
      const response = await api.get("/document/my-documents");
      setIsLoading(false);

      if (response.data.success) {
        setDocuments(response.data.documents || []);
      } else {
        toast.error("Failed to fetch documents");
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to fetch documents");
      setIsLoading(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + " " + sizes[i];
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const getFileIcon = (fileType) => {
    switch (fileType) {
      case "PDF":
        return "fa fa-file-pdf text-red-500";
      case "Image":
        return "fa fa-file-image text-green-500";
      case "Document":
        return "fa fa-file-word text-blue-500";
      case "Spreadsheet":
        return "fa fa-file-excel text-green-600";
      case "Text":
        return "fa fa-file-alt text-gray-500";
      case "Archive":
        return "fa fa-file-archive text-yellow-500";
      default:
        return "fa fa-file text-gray-500";
    }
  };

  const handleViewFile = (doc) => {
    const fileUrl = `${process.env.REACT_APP_SERVER_ADDRESS || "http://localhost:8001"}/api/document/file/${doc.file_name}`;
    window.open(fileUrl, "_blank");
  };

  const handleDownloadFile = (doc) => {
    const fileUrl = `${process.env.REACT_APP_SERVER_ADDRESS || "http://localhost:8001"}/api/document/file/${doc.file_name}`;
    const link = document.createElement("a");
    link.href = fileUrl;
    link.download = doc.original_name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="px-6 mt-20 mx-10 pb-20">
      {isLoading && <Spinner />}

      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-600">My Documents</h1>
      </div>

      {/* Documents Table */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="p-4 border-b">
          <h2 className="text-lg font-semibold text-gray-700">Documents</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead className="bg-gray-200">
              <tr>
                <th className="p-3 text-left font-bold text-gray-700">#</th>
                <th className="p-3 text-left font-bold text-gray-700">Document Name</th>
                <th className="p-3 text-left font-bold text-gray-700">File Type</th>
                <th className="p-3 text-left font-bold text-gray-700">File Size</th>
                <th className="p-3 text-left font-bold text-gray-700">Uploaded Date</th>
                <th className="p-3 text-left font-bold text-gray-700">Uploaded By</th>
                <th className="p-3 text-center font-bold text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody>
              {documents.length === 0 ? (
                <tr>
                  <td colSpan={7} className="p-4 text-center text-gray-500">
                    No documents found
                  </td>
                </tr>
              ) : (
                documents.map((doc, index) => (
                  <tr key={doc._id} className="border-b border-gray-200 hover:bg-gray-50">
                    <td className="p-3 text-gray-700">{index + 1}</td>
                    <td className="p-3 text-gray-700">{doc.document_name}</td>
                    <td className="p-3 text-gray-700">
                      <i className={`${getFileIcon(doc.file_type)} mr-2`}></i>
                      {doc.file_type}
                    </td>
                    <td className="p-3 text-gray-700">{formatFileSize(doc.file_size)}</td>
                    <td className="p-3 text-gray-700">{formatDate(doc.createdAt)}</td>
                    <td className="p-3 text-gray-700">
                      {doc.uploaded_by
                        ? `${doc.uploaded_by.firstName || ""} ${doc.uploaded_by.lastName || ""}`.trim() ||
                          doc.uploaded_by.preferredName ||
                          doc.uploaded_by.email
                        : "-"}
                    </td>
                    <td className="p-3 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => handleViewFile(doc)}
                          className="px-3 py-1 bg-blue-500 text-white rounded text-sm hover:bg-blue-600 transition"
                          title="View File"
                        >
                          <i className="fa fa-eye mr-1"></i>
                          View
                        </button>
                        <button
                          onClick={() => handleDownloadFile(doc)}
                          className="px-3 py-1 bg-gray-500 text-white rounded text-sm hover:bg-gray-600 transition"
                          title="Download File"
                        >
                          <i className="fa fa-download mr-1"></i>
                          Download
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

